import { ONE_CHANCE_SETTINGS } from "app/data/game-settings";
import { UNKNOWN_LETTER_CHAR } from "./oneChanceSpellingUtils";

export interface OneChanceState {
  isOnIntro: boolean;
  currentSpelling: string;
  revealsUsed: number;
  definitionUnlocked: boolean;
  originUnlocked: boolean;
  lengthRevealed: boolean;
  spellingSubmitted: boolean;
  scoreMultiplier: number;
  originalScore: number;
}

export const INITIAL_STATE: OneChanceState = {
  isOnIntro: true,
  currentSpelling: UNKNOWN_LETTER_CHAR.repeat(
    ONE_CHANCE_SETTINGS.numLetterBoxes,
  ),
  revealsUsed: 0,
  definitionUnlocked: false,
  originUnlocked: false,
  lengthRevealed: false,
  spellingSubmitted: false,
  scoreMultiplier: ONE_CHANCE_SETTINGS.initMult,
  originalScore: 0,
};

export const OneChanceAction = {
  INTRO_CLOSED: "INTRO_CLOSED",
  SPELLING_UPDATED: "PLAYER_INPUT_UPDATED",
  LETTER_REVEALED: "LETTER_REVEALED",
  DEFINITION_UNLOCKED: "DEFINITION_UNLOCKED",
  ORIGIN_UNLOCKED: "ORIGIN_UNLOCKED",
  LENGTH_REVEALED: "LENGTH_REVEALED",
  SPELLING_SUBMITTED: "SPELLING_SUBMITTED",
  ANSWERED_INCORRECTLY: "ANSWERED_INCORRECTLY",
  ROUND_COMPLETED: "ROUND_COMPLETED",
} as const;

export type OneChanceAction =
  | {
      type: typeof OneChanceAction.INTRO_CLOSED;
    }
  | {
      type: typeof OneChanceAction.LETTER_REVEALED;
    }
  | {
      type: typeof OneChanceAction.SPELLING_UPDATED;
      payload: {
        newSpelling: string;
      };
    }
  | {
      type: typeof OneChanceAction.DEFINITION_UNLOCKED;
    }
  | {
      type: typeof OneChanceAction.ORIGIN_UNLOCKED;
    }
  | {
      type: typeof OneChanceAction.LENGTH_REVEALED;
    }
  | {
      type: typeof OneChanceAction.SPELLING_SUBMITTED;
      payload: {
        preMultScore: number;
        userSpelling: string;
        isCorrect: boolean;
      };
    }
  | { type: typeof OneChanceAction.ROUND_COMPLETED };

export function oneChanceReducer(
  state: OneChanceState,
  action: OneChanceAction,
) {
  switch (action.type) {
    case OneChanceAction.INTRO_CLOSED:
      return {
        ...state,
        isOnIntro: false,
      };
    case OneChanceAction.LETTER_REVEALED: {
      const multCost =
        ONE_CHANCE_SETTINGS.letterRevealMultCost +
        state.revealsUsed * ONE_CHANCE_SETTINGS.letterRevealMultCostIncrease;

      return {
        ...state,
        scoreMultiplier: Math.max(
          state.scoreMultiplier - multCost,
          ONE_CHANCE_SETTINGS.minMult,
        ),
        revealsUsed: state.revealsUsed + 1,
      };
    }
    case OneChanceAction.SPELLING_UPDATED:
      return {
        ...state,
        currentSpelling: action.payload.newSpelling,
      };

    case OneChanceAction.DEFINITION_UNLOCKED:
      return {
        ...state,
        definitionUnlocked: true,
        scoreMultiplier: Math.max(
          state.scoreMultiplier - ONE_CHANCE_SETTINGS.definitionMultCost,
          ONE_CHANCE_SETTINGS.minMult,
        ),
      };
    case OneChanceAction.ORIGIN_UNLOCKED:
      return {
        ...state,
        originUnlocked: true,
        scoreMultiplier: Math.max(
          state.scoreMultiplier - ONE_CHANCE_SETTINGS.originMultCost,
          ONE_CHANCE_SETTINGS.minMult,
        ),
      };
    case OneChanceAction.LENGTH_REVEALED:
      return {
        ...state,
        lengthRevealed: true,
        scoreMultiplier: Math.max(
          state.scoreMultiplier - ONE_CHANCE_SETTINGS.lengthMultCost,
          ONE_CHANCE_SETTINGS.minMult,
        ),
      };
    case OneChanceAction.SPELLING_SUBMITTED:
      return {
        ...state,
        spellingSubmitted: true,
        userSpelling: action.payload.userSpelling,
        originalScore: action.payload.preMultScore,
        scoreMultiplier: action.payload.isCorrect ? state.scoreMultiplier : 1.0,
      };
    case OneChanceAction.ROUND_COMPLETED:
      return INITIAL_STATE;
    default:
      return state;
  }
}
